import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Check, Plus } from "lucide-react";
import { PageContainer, PageHeader, Card, Field, Input, Button, inputClass } from "../components/kit";

export default function NewJob() {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [skills, setSkills] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();

  const currentSkills = skills.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  const isAdded = (skill) => currentSkills.includes(skill.toLowerCase());

  const toggleSkill = (skill) => {
    const existing = skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (isAdded(skill)) {
      setSkills(existing.filter((s) => s.toLowerCase() !== skill.toLowerCase()).join(", "));
    } else {
      setSkills(existing.length ? existing.join(", ") + ", " + skill : skill);
    }
  };

  const suggestSkills = async () => {
    if (!desc.trim() || suggesting) return;
    setSuggesting(true);
    setSuggestions([]);
    try {
      const { data } = await api.post("/jobs/suggest-skills", { description: desc });
      setSuggestions(data.skills || []);
      if (!data.skills?.length) toast.info("No skills detected — try a more detailed description");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not fetch suggestions");
    } finally { setSuggesting(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const required_skills = skills.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/jobs", { title, description: desc, required_skills });
      toast.success("Role posted");
      navigate(`/jobs/${data.job_id}/candidates`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create job");
    } finally { setSubmitting(false); }
  };

  return (
    <PageContainer width="mid">
      <PageHeader eyebrow="New Role" title="Post a role." />

      <Card padding="p-8">
        <form onSubmit={submit} className="space-y-6">
          <Field label="Title">
            <Input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Senior Backend Engineer" data-testid="job-title" />
          </Field>

          <Field label="Job Description">
            <textarea className={`${inputClass} min-h-[220px] resize-y`} required value={desc}
              onChange={(e) => { setDesc(e.target.value); setSuggestions([]); }}
              placeholder="What the role does, must-haves, nice-to-haves…" data-testid="job-desc" />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-[#0F172A]">Required skills (comma separated)</label>
              <button type="button" onClick={suggestSkills} disabled={!desc.trim() || suggesting}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-150 ${
                  desc.trim() && !suggesting
                    ? "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/50 hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED]"
                    : "bg-secondary text-muted-foreground border-border cursor-not-allowed opacity-50"
                }`}>
                <Sparkles className={`w-3.5 h-3.5 ${suggesting ? "animate-pulse" : ""}`} />
                {suggesting ? "Detecting…" : "Suggest from description"}
              </button>
            </div>
            <Input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="Python, FastAPI, MongoDB, AWS" data-testid="job-skills" />

            {suggestions.length > 0 && (
              <div className="mt-3">
                <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-2">SUGGESTED SKILLS — click to add or remove</div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((skill) => {
                    const added = isAdded(skill);
                    return (
                      <button key={skill} type="button" onClick={() => toggleSkill(skill)}
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-all duration-150 ${
                          added
                            ? "bg-[#0025D8] text-white border-[#0025D8]"
                            : "bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/40 hover:bg-[#0025D8] hover:text-white hover:border-[#0025D8]"
                        }`}>
                        {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                        {skill}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <Button type="submit" disabled={submitting} loading={submitting} size="lg" data-testid="publish-job">
            {submitting ? "Publishing…" : "Publish role"}
          </Button>
        </form>
      </Card>
    </PageContainer>
  );
}
